import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  ShoppingBag, 
  Store,
  Box,
  Archive,
  Factory,
  Beaker,
  Truck,
  Sprout,
  Droplet,
  Circle,
  ChevronRight,
  ShoppingCart,
  Settings
} from "lucide-react";
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

interface MenuItem {
  label: string;
  path: string;
  icon: React.ComponentType<any>;
  adminOnly: boolean;
  section: string;
  children?: {
    label: string;
    path: string;
    adminOnly: boolean;
  }[];
}

interface SidebarMenuItemsProps {
  isAdmin: boolean;
}

const menuItems: MenuItem[] = [
  { 
    label: "Dashboard", 
    path: "/dashboard", 
    icon: LayoutDashboard,
    adminOnly: true,
    section: "admin"
  },
  { 
    label: "Users", 
    path: "/admin/users", 
    icon: Users,
    adminOnly: true,
    section: "admin"
  },
  { 
    label: "Orders", 
    path: "/admin/orders", 
    icon: ShoppingCart,
    adminOnly: true,
    section: "admin"
  },
  {
    label: "Marketplace",
    path: "/marketplace",
    icon: Store,
    adminOnly: false,
    section: "marketplace"
  },
  { 
    label: "Marketplace Settings", 
    path: "/marketplace-settings", 
    icon: Settings,
    adminOnly: true,
    section: "marketplace"
  },
  { 
    label: "Cultivars", 
    path: "/admin/cultivars", 
    icon: ShoppingBag,
    adminOnly: true,
    section: "admin"
  },
  { 
    label: "Batches", 
    path: "/admin/batches", 
    icon: Sprout,
    adminOnly: true,
    section: "admin"
  },
  { 
    label: "Hardware", 
    path: "/admin/hardware",
    icon: Box,
    adminOnly: true,
    section: "admin"
  },
];

export const SidebarMenuItems = memo<SidebarMenuItemsProps>(({ isAdmin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useSidebar();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const renderMenuItem = (item: MenuItem) => {
    // Skip admin-only items for non-admin users
    if (item.adminOnly && !isAdmin) {
      return null;
    }

    const isActive = location.pathname === item.path;
    const isParentActive = item.children?.some((child) => 
      location.pathname.startsWith(child.path) && (!child.adminOnly || isAdmin)
    );

    // Filter children based on admin status
    const filteredChildren = item.children?.filter((child) => 
      !child.adminOnly || isAdmin
    );

    const IconComponent = item.icon;

    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton
          isActive={isActive || isParentActive}
          onClick={() => handleNavigate(item.path)}
          className={cn(
            "sidebar-menu-item flex justify-between items-center",
            isActive || isParentActive ? "sidebar-menu-item-active" : "sidebar-menu-item-default"
          )}
        >
          {state === "collapsed" ? (
            <div className="w-full flex justify-center">
              <IconComponent className="sidebar-icon" />
            </div>
          ) : (
            <div className="sidebar-icon-container">
              <IconComponent className="sidebar-icon" />
              <span>{item.label}</span>
            </div>
          )}
          
          {filteredChildren && filteredChildren.length > 0 && state !== "collapsed" && (
            <ChevronRight className="ml-auto h-4 w-4" />
          )}
        </SidebarMenuButton>
        
        {filteredChildren && filteredChildren.length > 0 && (
          <SidebarMenuSub>
            <div className="sidebar-submenu">
              {filteredChildren.map((child) => (
                <SidebarMenuSubItem key={child.path}>
                  <SidebarMenuSubButton
                    isActive={location.pathname === child.path}
                    onClick={() => handleNavigate(child.path)}
                    className={cn(
                      "sidebar-submenu-item",
                      location.pathname === child.path
                        ? "sidebar-submenu-item-active"
                        : "sidebar-submenu-item-default"
                    )}
                  >
                    <span>{child.label}</span>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </div>
          </SidebarMenuSub>
        )}
      </SidebarMenuItem>
    );
  };

  // Group menu items by section
  const menuSections = menuItems
    .filter(item => !item.adminOnly || isAdmin)
    .reduce<Record<string, MenuItem[]>>((acc, item) => {
      const section = item.section || 'other';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(item);
      return acc;
    }, {});

  const sectionOrder = ['admin', 'formulations', 'marketplace', 'other'];

  const getSectionTitle = (section: string) => {
    switch (section) {
      case 'admin': return 'Admin';
      case 'formulations': return 'Formulations';
      case 'marketplace': return 'Marketplace';
      default: return 'Other';
    }
  };

  return (
    <SidebarMenu>
      {sectionOrder.map((sectionKey) => {
        const items = menuSections[sectionKey];
        if (!items || items.length === 0) return null;
        
        return (
          <div key={sectionKey}>
            {items.map((item) => renderMenuItem(item))}
          </div>
        );
      })}
    </SidebarMenu>
  );
});

SidebarMenuItems.displayName = 'SidebarMenuItems';